import * as React from 'react';
import AssignmentLateOutlined from '@material-ui/icons/AssignmentLateOutlined';
import BusinessIcon from '@material-ui/icons/Business';
import MailOutlinedIcon from '@material-ui/icons/MailOutlined';
import moment from 'moment';
import { AnchorListItem } from '@pec/aion-ui-components/components/Nav/AnchorListItem';
import { Box, List, MenuItem, Typography } from '@material-ui/core';
import { CountryIso2 } from '../../helpers/enums';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { DropdownMenu } from '../DropdownMenu';
import { fetchExpiredQuestions, fetchExpiringQuestions } from 'slices/questions';
import { fetchInsurancesV2 } from 'slices/insurancesV2';
import { fetchOrganizationInsuranceSectionsV2 } from 'slices/organizationInsuranceSectionsV2';
import { fetchProgramReviewsCount } from 'slices/programReviews';
import { fetchVerificationsIfNeeded } from 'actions/verifications';
import { IExpiringQuestion } from 'interfaces/questions';
import { IInsuranceV2 } from 'interfaces/insurance';
import { IVerification } from 'interfaces/verification';
import { localizeDate } from '@pec/aion-ui-i18next/helpers/localize';
import { PendingOutlinedIcon } from './PendingOutlinedIcon';
import { uniqBy } from 'lodash';
import { useAppDispatch, useTypedSelector } from '../../combineReducers';
import { useOrganizationProfileNotifications } from '@pec/aion-ui-components/hooks/useOrganizationProfileNotifications';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Widget } from '../Widget';

type RouteParams = {
  organizationId: string;
};

type ToDoItem = {
  key: string;
  label: string;
  icon: JSX.Element;
  link: string;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    icon: {
      width: 24,
      color: 'var(--primary-blue-six)'
    },
    menuTitle: {
      color: 'var(--primary-blue-six)',
      display: 'flex',
      gap: `${theme.spacing(1)}px`,
      padding: theme.spacing(1.5, 0)
    },
    menuTitleIcon: {
      color: 'var(--primary-blue-six)'
    },
    accordion: {
      boxShadow: 'none',
      '&.Mui-expanded': {
        margin: 0
      },
      '&:before': {
        backgroundColor: 'transparent'
      }
    },
    accordionSummary: {
      padding: theme.spacing(0, 1),
      '&.Mui-expanded': {
        minHeight: 0
      },
      '& .MuiAccordionSummary-content.Mui-expanded': {
        margin: theme.spacing(1.5, 0)
      }
    },
    accordionDetails: {
      flexDirection: 'column',
      padding: theme.spacing(0, 0, 0, 2)
    }
  })
);

export const ToDoList: React.FC = () => {
  const { statuses, isFetching: isFetchingStatuses, isProfileCompleted } = useOrganizationProfileNotifications();
  const { organizationId } = useParams<RouteParams>();
  const dispatch = useAppDispatch();
  const { insurancesV2: insurances, isFetching: isFetchingInsurances, error: fetchInsurancesError } = useTypedSelector(
    state => state.insurancesV2
  );
  const { verifications, isFetching: isFetchingVerifications, error: fetchVerificationsError } = useTypedSelector(
    state => state.verifications
  );
  const {
    mainSubscription: { countryISO2 }
  } = useTypedSelector(state => state.mainSubscription);

  const {
    expiredQuestions,
    expiringQuestions,
    isFetching: isFetchingQuestions,
    error: fetchQuestionsError
  } = useTypedSelector(state => state.questions);
  const { organizationInsuranceSectionsV2 } = useTypedSelector(state => state.organizationInsuranceSectionsV2);
  const { count: programReviewsCount, isFetching: isFetchingProgramReviews } = useTypedSelector(
    state => state.programReviews
  );
  const { t } = useTranslation('subscriber');
  const classes = useStyles();

  React.useEffect(() => {
    if (organizationId) {
      dispatch(fetchExpiredQuestions(organizationId));
      dispatch(fetchExpiringQuestions(organizationId));
    }
  }, [organizationId, dispatch]);

  React.useEffect(() => {
    const shouldFetchInsurance = ![CountryIso2.GB, CountryIso2.AU].includes(countryISO2 as CountryIso2);

    if (organizationId && shouldFetchInsurance) {
      dispatch(fetchOrganizationInsuranceSectionsV2(organizationId));
      dispatch(fetchInsurancesV2(organizationId));
      dispatch(fetchVerificationsIfNeeded(organizationId));
    }
  }, [organizationId, dispatch, countryISO2]);

  React.useEffect(() => {
    if (organizationId) {
      dispatch(fetchProgramReviewsCount(organizationId));
    }
  }, [organizationId, dispatch]);

  const toDoItems: Record<string, ToDoItem> = {
    profile: {
      key: 'profile',
      label: t('newDashboard.toDoWidget.companyProfile', 'Complete company profile'),
      icon: <BusinessIcon classes={{ root: classes.icon }} />,
      link: `/organizations/${organizationId}/profile`
    },
    myConnections: {
      key: 'myConnections',
      label: t('newDashboard.toDoWidget.reviewPendings', 'Review pending connection(s)'),
      icon: <MailOutlinedIcon classes={{ root: classes.icon }} />,
      link: `/organizations/${organizationId}/my-connections`
    }
  };

  const renderToDoItem = (item: ToDoItem) => {
    const { key, label, icon, link } = item;

    return (
      <AnchorListItem
        key={key}
        icon={icon}
        href={link}
        style={{
          paddingLeft: 0
        }}
      >
        <Typography variant="body1" color="primary">
          {label}
        </Typography>
      </AnchorListItem>
    );
  };

  const renderToDoItems = () => {
    const toDoList = {
      profile: [...statuses.contactInformationSection, ...statuses.operationDetailsSection],
      myConnections: statuses.hiringClientSection
    };

    return Object.entries(toDoList).map(([key, statuses]) => {
      if (statuses.some(([_, value]) => value === false)) {
        return renderToDoItem(toDoItems[key]);
      }
      return null;
    });
  };

  const renderVerifications = ({
    verifications,
    isExpired,
    hasError
  }: {
    verifications: IVerification[];
    isExpired: boolean;
    hasError: boolean;
  }) => {
    if (!verifications || verifications.length === 0 || hasError) {
      return null;
    }
    const groupLabel = isExpired
      ? t('newDashboard.toDoWidget.updateExpiredVerifications', 'Update expired verifications')
      : t('newDashboard.toDoWidget.updateExpiringVerifications', 'Update verifications - expiring soon');
    const Icon = isExpired ? AssignmentLateOutlined : PendingOutlinedIcon;
    return (
      <DropdownMenu
        title={
          <Box className={classes.menuTitle}>
            <Icon className={classes.menuTitleIcon} />
            <Typography variant="body1">
              {groupLabel} ({verifications.length})
            </Typography>
          </Box>
        }
      >
        {verifications.map((verification, index) => {
          const expirationDate = verification.expirationDate && localizeDate(verification.expirationDate, t);
          return (
            <MenuItem
              key={`${verification.name}-${index}`}
              component="a"
              href={`/organizations/${organizationId}/questionnaire-v2/${verification.questionSectionId}`}
            >
              {t(
                isExpired ? 'newDashboard.toDoWidget.expiredOn' : 'newDashboard.toDoWidget.expiresOn',
                isExpired ? '{{name}} expired on {{date}}' : '{{name}} expires on {{date}}',
                {
                  name: verification.name,
                  date: expirationDate
                }
              )}
            </MenuItem>
          );
        })}
      </DropdownMenu>
    );
  };

  const renderInsurances = ({
    insurances,
    isExpired,
    hasError
  }: {
    insurances: IInsuranceV2[];
    isExpired: boolean;
    hasError: boolean;
  }) => {
    if (!insurances || insurances.length === 0 || hasError) {
      return null;
    }
    const groupLabel = isExpired
      ? t('newDashboard.toDoWidget.updateExpiredInsurance', 'Update expired insurance')
      : t('newDashboard.toDoWidget.updateExpiringInsurance', 'Update insurance - expiring soon');
    const Icon = isExpired ? AssignmentLateOutlined : PendingOutlinedIcon;
    return (
      <DropdownMenu
        title={
          <Box className={classes.menuTitle}>
            <Icon className={classes.menuTitleIcon} />
            <Typography variant="body1">
              {groupLabel} ({insurances.length})
            </Typography>
          </Box>
        }
      >
        {insurances.map((insurance, index) => {
          const expirationDate =
            insurance.earliestPolicyExpirationDateUtc && localizeDate(insurance.earliestPolicyExpirationDateUtc, t);
          return (
            <MenuItem
              key={`${insurance.organizationName}-${index}`}
              component="a"
              href={`/organizations/${organizationId}/questionnaire-v2/${insurance.questionSectionId}`}
            >
              {t(
                isExpired ? 'newDashboard.toDoWidget.expiredOn' : 'newDashboard.toDoWidget.expiresOn',
                isExpired ? '{{name}} expired on {{date}}' : '{{name}} expires on {{date}}',
                {
                  name: insurance.organizationName,
                  date: expirationDate
                }
              )}
            </MenuItem>
          );
        })}
      </DropdownMenu>
    );
  };

  const renderQuestions = ({
    questions,
    isExpired,
    hasError
  }: {
    questions: IExpiringQuestion[];
    isExpired: boolean;
    hasError: boolean;
  }) => {
    if (!questions || questions.length === 0 || hasError) return null;

    const groupLabel = isExpired
      ? t('newDashboard.toDoWidget.updateExpiredAnswers', 'Update expired answers')
      : t('newDashboard.toDoWidget.updateExpiringAnswers', 'Update expiring answers');
    const Icon = isExpired ? AssignmentLateOutlined : PendingOutlinedIcon;

    return (
      <DropdownMenu
        title={
          <Box className={classes.menuTitle}>
            <Icon className={classes.menuTitleIcon} />
            <Typography variant="body1">
              {groupLabel} ({questions.length})
            </Typography>
          </Box>
        }
      >
        {uniqBy(questions, 'sectionId').map(question => {
          const questionsCount = questions.filter(item => item.sectionId === question.sectionId).length;

          return (
            <MenuItem
              key={question.questionId}
              component="a"
              href={`/organizations/${organizationId}/questionnaire-v2/${question.sectionId}`}
            >
              {question.sectionName} ({questionsCount})
            </MenuItem>
          );
        })}
      </DropdownMenu>
    );
  };

  const renderProgramReviews = () => {
    if ((programReviewsCount || 0) === 0) {
      return null;
    }

    return (
      <AnchorListItem
        key="programReviews"
        icon={<AssignmentLateOutlined classes={{ root: classes.icon }} />}
        href={`/organizations/${organizationId}/safety-program-requirements`}
        style={{
          paddingLeft: 0
        }}
      >
        <Typography variant="body1" color="primary">
          {t('newDashboard.toDoWidget.reviewPrograms', 'Review Programs')} ({programReviewsCount})
        </Typography>
      </AnchorListItem>
    );
  };

  const expiredVerifications =
    verifications?.filter(({ expirationDate }) => expirationDate !== null && moment(expirationDate).isBefore()) || [];
  const expiringVerifications =
    verifications?.filter(
      ({ expirationDate, expiringPeriodDays }) =>
        expirationDate !== null && moment(expirationDate).isBetween(moment(), moment().add(expiringPeriodDays, 'days'))
    ) || [];
  const insuranceWithSections = insurances.map(ins => {
    const section = organizationInsuranceSectionsV2.find(section => section.organizationId === ins.organizationId);
    return { ...ins, questionSectionId: section?.sectionId };
  });
  const expiredInsurances =
    insuranceWithSections.filter(({ earliestPolicyExpirationDateUtc }) =>
      moment(earliestPolicyExpirationDateUtc).isBefore()
    ) || [];
  const expiringInsurances =
    insuranceWithSections.filter(({ earliestPolicyExpirationDateUtc }) =>
      moment(earliestPolicyExpirationDateUtc).isBetween(moment(), moment().add(30, 'days'))
    ) || [];

  const hasExpiredVerifications = expiredVerifications.length > 0;
  const hasExpiringVerifications = expiringVerifications.length > 0;
  const hasExpiredInsurances = expiredInsurances.length > 0;
  const hasExpiringInsurances = expiringInsurances.length > 0;
  const hasExpiredQuestions = expiredQuestions.length > 0;
  const hasExpiringQuestions = expiringQuestions.length > 0;
  const hasProgramReviews = (programReviewsCount || 0) > 0;

  if (
    isProfileCompleted &&
    !hasExpiredVerifications &&
    !hasExpiringVerifications &&
    !hasExpiredInsurances &&
    !hasExpiringInsurances &&
    !hasExpiredQuestions &&
    !hasExpiringQuestions &&
    !hasProgramReviews
  ) {
    return null;
  }

  const isFetching =
    isFetchingStatuses ||
    isFetchingInsurances ||
    isFetchingVerifications ||
    isFetchingQuestions ||
    isFetchingProgramReviews;

  return (
    <Widget title={t('newDashboard.toDoWidget.title', 'To do')} isFetching={isFetching}>
      <List>
        {!isProfileCompleted ? renderToDoItems() : null}
        {renderProgramReviews()}
        {renderVerifications({
          verifications: expiredVerifications,
          isExpired: true,
          hasError: !!fetchVerificationsError
        })}
        {renderVerifications({
          verifications: expiringVerifications,
          isExpired: false,
          hasError: !!fetchVerificationsError
        })}
        {renderInsurances({ insurances: expiredInsurances, isExpired: true, hasError: !!fetchInsurancesError })}
        {renderInsurances({ insurances: expiringInsurances, isExpired: false, hasError: !!fetchInsurancesError })}
        {renderQuestions({ questions: expiredQuestions, isExpired: true, hasError: !!fetchQuestionsError })}
        {renderQuestions({ questions: expiringQuestions, isExpired: false, hasError: !!fetchQuestionsError })}
      </List>
    </Widget>
  );
};
